import type { IFilesystem, FileMetadata } from '@livx.cc/wcli/core';
import { globToRegExp } from './tools.structured';
import { toVfsPath } from './hostPath';

/**
 * Containment boundary A — a policy decorator over ANY IFilesystem.
 *
 * The agent only ever sees an IFilesystem, so what it can touch is exactly what we
 * mount. This jail:
 *   - hides + blocks `deny` paths entirely (secrets: .env, keys, .git, …) — invisible
 *     to read/stat/exists/readDir, unwritable;
 *   - makes `readonly` paths readable but unwritable (the "constitution": pinned
 *     grader/suite/criteria the self-evolve agent must never edit);
 *   - optionally `confineTo` a set of subtrees (anything outside is denied);
 *   - normalizes paths first, so `..` traversal (`/src/../.env`) is caught after resolution;
 *   - reports every violation via `onViolation` (audit trail / circuit-breaker hook).
 *
 * Crucial limitation: this guards what the AGENT'S TOOLS touch. It does NOT contain
 * code executed against the real disk at eval time (the grader imports agent-written
 * code) — that is boundary B (a sandboxed subprocess). See mind/08-self-evolve.md.
 */
export class JailedFilesystem implements IFilesystem {
  public options: JailOptions;
  private denyRe: RegExp[];
  private roRe: RegExp[];
  private confineRe?: RegExp[];

  constructor(private inner: IFilesystem, options?: Partial<JailOptions>) {
    this.options = { ...new JailOptions(), ...options };
    // case-insensitive so /.ENV, /Id_Rsa, … can't slip past a lowercase rule.
    this.denyRe = this.options.deny.map((g) => globToRegExp(g, true));
    this.roRe = this.options.readonly.map((g) => globToRegExp(g, true));
    this.confineRe = this.options.confineTo?.map((g) => globToRegExp(g, true));
  }

  /** Resolved VFS path — host-path syntax (Windows `C:\…`, backslashes) is normalized FIRST, so the
   *  deny/readonly/confine globs always match the path that is actually opened. */
  private abs(path: string): string {
    return this.inner.resolvePath(toVfsPath(path), this.inner.getCwd());
  }

  /** Throw if reading `path` is not permitted (denied or out of confinement). */
  private guardRead(op: string, path: string): string {
    const abs = this.abs(path);
    if (this.confineRe && !this.confineRe.some((r) => r.test(abs))) return this.violate(op, abs, 'confine');
    if (this.denyRe.some((r) => r.test(abs))) return this.violate(op, abs, 'deny');
    return abs;
  }

  /** Throw if writing/deleting `path` is not permitted (denied, readonly, or out of confinement). */
  private guardWrite(op: string, path: string): string {
    const abs = this.guardRead(op, path); // write implies read access first
    if (this.roRe.some((r) => r.test(abs))) return this.violate(op, abs, 'readonly');
    return abs;
  }

  private violate(op: string, abs: string, rule: string): never {
    this.note(op, abs, rule);
    // Read denials masquerade as "not found" to avoid confirming a secret's existence.
    if (rule === 'deny' || rule === 'confine') throw new Error(`File not found: ${abs}`);
    throw new Error(`Blocked by filesystem jail (${rule}): ${abs}`);
  }

  /** Fire the audit hook without throwing (for boolean probes that must still return false). */
  private note(op: string, abs: string, rule: string): void {
    this.options.onViolation?.({ op, path: abs, rule });
  }

  /** Is `abs` (already-resolved) visible? If not, fire the audit hook (op given) and return false. */
  private visible(abs: string, op?: string): boolean {
    if (this.confineRe && !this.confineRe.some((r) => r.test(abs))) { if (op) this.note(op, abs, 'confine'); return false; }
    if (this.denyRe.some((r) => r.test(abs))) { if (op) this.note(op, abs, 'deny'); return false; }
    return true;
  }

  async readFile(path: string): Promise<string> { return this.inner.readFile(this.guardRead('readFile', path)); }
  /** Forward the optional binary-read capability (if the inner fs has it), jailed like readFile. */
  async readFileBytes(path: string): Promise<Uint8Array> {
    const inner = this.inner as { readFileBytes?: (p: string) => Promise<Uint8Array> };
    if (typeof inner.readFileBytes !== 'function') throw new Error('binary read not supported by this filesystem');
    return inner.readFileBytes(this.guardRead('readFileBytes', path));
  }
  /** Forward the optional real-path capability (image REFS), jail-guarded like readFile. */
  realPath(path: string): string {
    const inner = this.inner as { realPath?: (p: string) => string };
    if (typeof inner.realPath !== 'function') throw new Error('realPath not supported by this filesystem');
    return inner.realPath(this.guardRead('realPath', path));
  }
  async writeFile(path: string, content: string): Promise<void> { return this.inner.writeFile(this.guardWrite('writeFile', path), content); }
  async deleteFile(path: string): Promise<void> { return this.inner.deleteFile(this.guardWrite('deleteFile', path)); }
  async createDir(path: string): Promise<void> { return this.inner.createDir(this.guardWrite('createDir', path)); }
  async stat(path: string): Promise<FileMetadata> { return this.inner.stat(this.guardRead('stat', path)); }

  // Boolean probes stay invisible (return false on a denied path) BUT still fire the
  // audit hook — otherwise an agent could enumerate secrets via exists() without
  // ever tripping the circuit breaker.
  async exists(path: string): Promise<boolean> {
    const abs = this.abs(path);
    return this.visible(abs, 'exists') ? this.inner.exists(abs) : false;
  }
  async isDirectory(path: string): Promise<boolean> {
    const abs = this.abs(path);
    return this.visible(abs, 'isDirectory') ? this.inner.isDirectory(abs) : false;
  }
  async isFile(path: string): Promise<boolean> {
    const abs = this.abs(path);
    return this.visible(abs, 'isFile') ? this.inner.isFile(abs) : false;
  }

  /** List a directory, filtering out entries the policy hides. */
  async readDir(path: string): Promise<string[]> {
    const abs = this.guardRead('readDir', path);
    const entries = await this.inner.readDir(abs);
    return entries.filter((name) => this.visible(abs === '/' ? `/${name}` : `${abs}/${name}`));
  }

  resolvePath(path: string, cwd?: string): string { return this.inner.resolvePath(toVfsPath(path), cwd ?? this.inner.getCwd()); }
  getCwd(): string { return this.inner.getCwd(); }
  /** Guard cwd too: the jail must never be able to "stand" on a denied/out-of-confine dir. */
  setCwd(path: string): void {
    const abs = this.abs(path);
    if (!this.visible(abs, 'setCwd')) throw new Error(`File not found: ${abs}`);
    this.inner.setCwd(abs);
  }
}

/** Default denylist: secrets and VCS internals that must never be read or written.
 *  Patterns are matched case-insensitively (so /.ENV is covered too). */
export const DEFAULT_DENY: string[] = [
  // env / config secrets
  '**/.env', '**/.env.*', '**/.npmrc', '**/.netrc',
  // private keys & certs
  '**/*.pem', '**/*.key', '**/*.crt', '**/*.cert', '**/*.p12', '**/*.pfx',
  '**/id_rsa', '**/id_rsa.*', '**/id_ed25519', '**/id_ed25519.*',
  '**/id_dsa', '**/id_dsa.*', '**/id_ecdsa', '**/id_ecdsa.*',
  // credential stores (dir AND contents)
  '**/.ssh', '**/.ssh/**', '**/.aws', '**/.aws/**',
  '**/.docker', '**/.docker/**', '**/.dockercfg',
  '**/.kube', '**/.kube/**', '**/.kubeconfig',
  '**/credentials', '**/.credentials', '**/secrets.json',
  // VCS internals + git config (can carry tokens / remote creds)
  '**/.git', '**/.git/**', '**/.gitconfig', '**/.gitmodules',
];

export class JailOptions {
  /** Globs that are invisible and unwritable (secrets). */
  deny: string[] = [...DEFAULT_DENY];
  /** Globs readable but not writable/deletable (the pinned constitution). */
  readonly: string[] = [];
  /** If set, only paths matching these globs are accessible at all. */
  confineTo?: string[];
  /** Audit hook fired on every blocked operation (drives the circuit breaker). */
  onViolation?: (v: { op: string; path: string; rule: string }) => void;
}
